import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { Link, useLoaderData, useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import prisma from "~/utils/db.server";
import { formatStorefrontPrice } from "~/utils/money";
import { resolveMarket } from "~/services/markets/marketDetection.server";
import { displayTitle as titleForLocale, renameProductTitle } from "~/utils/productTitle";
import { getMarketPrices } from "~/services/markets/marketPricing.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = detectLocale(request.url);
  const shop = await prisma.shop.findFirst();

  // Fetch featured collections with products for the lookbook
  const collections = await prisma.collection.findMany({
    where: { shopId: shop?.id, isActive: true },
    orderBy: { sortOrder: "asc" },
    take: 6,
  });

  // Fetch products for each collection (3 per collection for the lookbook grid)
  const collectionProducts = await Promise.all(
    collections.map(async (col) => {
      const products = await prisma.product.findMany({
        where: {
          shopId: shop?.id,
          collectionId: col.id,
          isRetailEnabled: true,
          status: "ACTIVE",
        },
        select: {
          id: true,
          handle: true,
          title: true,
          titleEn: true,
          featuredImageUrl: true,
          retailPrice: true,
          images: true,
          variants: {
            select: { price: true },
            orderBy: { price: "asc" },
            take: 1,
          },
        },
        take: 4,
      });
      return {
        collection: {
          id: col.id,
          name: col.name,
          description: col.description,
          coverImageUrl: col.coverImageUrl,
        },
        products: products.map((p) => ({
          id: p.id,
          handle: p.handle,
          title: renameProductTitle(p.title),
          titleEn: p.titleEn ? renameProductTitle(p.titleEn) : p.titleEn,
          imageUrl: p.featuredImageUrl,
          price: Number(p.retailPrice || p.variants?.[0]?.price || 0),
          images: Array.isArray(p.images) ? p.images : [],
        })),
      };
    })
  );

  // Get some hero products for the lookbook banner
  const heroProducts = await prisma.product.findMany({
    where: { shopId: shop?.id, isRetailEnabled: true, status: "ACTIVE" },
    select: {
      id: true,
      handle: true,
      title: true,
      titleEn: true,
      featuredImageUrl: true,
      retailPrice: true,
      variants: {
        where: { isAvailable: true },
        select: { price: true },
        orderBy: { price: "asc" },
        take: 1,
      },
    },
    orderBy: { createdAt: "desc" },
    take: 6,
  });

  // Apply market pricing
  const { market: lbMarket } = shop ? await resolveMarket(request, shop.id) : { market: null };
  const activeCurrency = lbMarket?.currency || shop?.currency || "VND";

  if (lbMarket && shop) {
    // Collect all variants from collection products and hero products
    const allProducts = [...collectionProducts.flatMap(cp => cp.products), ...heroProducts];
    // We only have first variant prices, need to get variant IDs for market lookup
    // Re-fetch with variant IDs for market pricing
    const productIds = allProducts.map(p => p.id);
    const productsWithVariants = await prisma.product.findMany({
      where: { id: { in: productIds } },
      select: { id: true, variants: { select: { id: true, price: true, compareAtPrice: true }, take: 1 } },
    });
    const variantsByProductId = new Map(productsWithVariants.map(p => [p.id, p.variants[0]]));
    const allVariants = productsWithVariants
      .filter(p => p.variants[0])
      .map(p => ({ id: p.variants[0].id, price: Number(p.variants[0].price), compareAtPrice: p.variants[0].compareAtPrice ? Number(p.variants[0].compareAtPrice) : null }));

    if (allVariants.length > 0) {
      const mpMap = await getMarketPrices(allVariants, lbMarket.marketId, shop.currency || "VND", shop.id);
      // Apply to collection products
      for (const cp of collectionProducts) {
        for (const p of cp.products) {
          const v = variantsByProductId.get(p.id);
          if (v) {
            const mp = mpMap.get(v.id);
            if (mp) (p as any).price = mp.price;
          }
        }
      }
      // Apply to hero products
      for (const p of heroProducts) {
        const v = variantsByProductId.get(p.id);
        if (v) {
          const mp = mpMap.get(v.id);
          if (mp) (p as any).retailPrice = mp.price;
        }
      }
    }
  }

  return json({
    locale,
    currency: activeCurrency,
    collectionProducts: collectionProducts.filter((cp) => cp.products.length > 0),
    heroProducts: heroProducts.map((p) => ({
      id: p.id,
      handle: p.handle,
      title: renameProductTitle(p.title),
      titleEn: p.titleEn ? renameProductTitle(p.titleEn) : p.titleEn,
      imageUrl: p.featuredImageUrl,
      price: Number(p.retailPrice || p.variants?.[0]?.price || 0),
    })),
  }, {
    headers: {
      "Cache-Control": "public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400",
    },
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = data?.locale || "vi";
  const description =
    locale === "vi"
      ? "Lookbook DIOLO 001 — trang phục pickleball nam, chụp trên sân đêm ở Sài Gòn."
      : "The DIOLO 001 lookbook — men's pickleball wear, shot on a night court in Saigon.";
  // Prefer a real collection hero image for og:image; fall back to a
  // first-product image so social crawlers always get a preview.
  const ogImage =
    (data as any)?.heroProducts?.[0]?.featuredImageUrl ||
    (data as any)?.collectionProducts?.[0]?.products?.[0]?.featuredImageUrl ||
    "/og/diolo-v2.png";
  return [
    { title: "Lookbook — Diolo" },
    { name: "description", content: description },
    { property: "og:title", content: "Lookbook — Diolo" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:image", content: ogImage },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Lookbook — Diolo" },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];
};

export default function LookbookPage() {
  const { locale, currency, collectionProducts, heroProducts } = useLoaderData<typeof loader>();
  const { locale: outletLocale } = useOutletContext<{ locale: Locale }>();
  const loc = outletLocale ?? locale;
  const fmtPrice = (amount: number) => formatStorefrontPrice(amount, currency);

  return (
    <div>
      {/* Hero Banner */}
      <div className="relative h-[60vh] sm:h-[70vh] overflow-hidden bg-luxury-charcoal">
        {heroProducts[0]?.imageUrl && (
          <img
            src={heroProducts[0].imageUrl}
            alt="Diolo Lookbook"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-16 px-4 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/70 mb-3">
            {loc === "vi" ? "BỘ SƯU TẬP" : "COLLECTION"}
          </p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-light text-white tracking-normal mb-4">
            Lookbook
          </h1>
          <p className="text-sm sm:text-base text-white/80 max-w-lg leading-relaxed">
            {loc === "vi"
              ? "Trang phục pickleball nam — từ sân đấu đến cuộc sống thường ngày. Born to Compete."
              : "Men's pickleball wear — from the court to everything after it. Born to Compete."}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-16">
        <Breadcrumbs locale={loc} items={[{ label: "Lookbook" }]} />

        {/* Collection Sections */}
        {collectionProducts.map((section, sectionIdx) => (
          <section key={section.collection.id} className="mb-20">
            {/* Collection Header */}
            <div className={`flex flex-col ${sectionIdx % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-8 lg:gap-16 items-center mb-12`}>
              {/* Collection Image */}
              <div className="w-full lg:w-1/2">
                {section.collection.coverImageUrl ? (
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={section.collection.coverImageUrl}
                      alt={section.collection.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ) : section.products[0]?.imageUrl ? (
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={section.products[0].imageUrl}
                      alt={section.collection.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/5] bg-luxury-sand/30" />
                )}
              </div>

              {/* Collection Info */}
              <div className="w-full lg:w-1/2 lg:py-12">
                <p className="text-[10px] uppercase tracking-[0.3em] text-luxury-stone mb-3">
                  {loc === "vi" ? "BỘ SƯU TẬP" : "COLLECTION"}
                </p>
                <h2 className="font-display text-3xl sm:text-4xl font-light text-luxury-charcoal tracking-normal mb-4">
                  {section.collection.name}
                </h2>
                {section.collection.description && (
                  <p className="text-sm text-luxury-stone leading-relaxed mb-6 max-w-md">
                    {section.collection.description}
                  </p>
                )}
                <Link
                  to={localePath("/products", loc)}
                  className="inline-block text-[11px] uppercase tracking-widest text-luxury-charcoal border-b border-luxury-charcoal pb-1 hover:text-luxury-black hover:border-luxury-black transition-colors"
                >
                  {loc === "vi" ? "MUA NGAY" : "SHOP NOW"} →
                </Link>
              </div>
            </div>

            {/* Shoppable Product Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {section.products.map((product) => (
                <Link
                  key={product.id}
                  to={localePath(`/products/${product.handle || product.id}`, loc)}
                  className="group"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-luxury-sand/20 mb-2">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt={titleForLocale(product.title, loc, product.titleEn)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-luxury-sand/30" />
                    )}
                  </div>
                  <h3 className="text-[12px] text-luxury-charcoal line-clamp-1 tracking-wide">
                    {titleForLocale(product.title, loc, product.titleEn)}
                  </h3>
                  <p className="text-[12px] text-luxury-stone">{fmtPrice(product.price)}</p>
                </Link>
              ))}
            </div>
          </section>
        ))}

        {/* Bottom CTA */}
        <div className="text-center py-16 border-t border-luxury-sand">
          <p className="text-[10px] uppercase tracking-[0.3em] text-luxury-stone mb-4">
            {loc === "vi" ? "KHÁM PHÁ THÊM" : "DISCOVER MORE"}
          </p>
          <h3 className="font-display text-2xl sm:text-3xl font-light text-luxury-charcoal tracking-normal mb-6">
            {loc === "vi" ? "Xem tất cả sản phẩm" : "View All Products"}
          </h3>
          <Link
            to={localePath("/products", loc)}
            className="sf-btn-primary inline-block"
          >
            {loc === "vi" ? "MUA SẮM NGAY" : "SHOP NOW"}
          </Link>
        </div>
      </div>
    </div>
  );
}
